/**
 * スプリント列の定義
 * Feature Flag (sprints-multi-select) に応じて単一/複数選択を切り替える
 */
import { ValueFormatterParams, ValueSetterParams } from 'ag-grid-community'
import { useCallback, useMemo } from 'react'
import { ColumnType } from '../../../containers/commons/AgGrid'
import MultiAutocompleteCell from '../../../containers/commons/AgGrid/components/cell/common/multiAutocomplete'
import { MultiAutocompleteCellEditor } from '../../../containers/BulkSheetView/components/cellEditor'
import { ServerSideSelectFilter } from '../../../containers/BulkSheetView/components/filter'
import Sprint, {
  SprintDetail,
  SprintStatus,
} from '../../../../lib/functions/sprint'
import { intl } from '../../../../i18n'
import { useSprint } from '../../../page-properties/bulksheet-properties/wbsItem'
import { defaultValueSetter } from '../../../page-properties/bulksheet-properties'
import { BulkSheetProperty } from '../../../page-properties/bulksheet-properties/bulksheetProperty'
import { ProjectPlanNewRow } from '../projectPlanNew'

// フィールド定数
export const FIELD_SPRINT = 'body.wbsItem.sprint'
export const FIELD_SPRINTS = 'body.wbsItem.sprints'

/** FINISHEDとCANCELEDは終了済みとして扱う */
const isFinishedStatus = (status: SprintStatus): boolean =>
  status === SprintStatus.FINISHED || status === SprintStatus.CANCELED

/**
 * スプリントをソートする
 * - 基本はnum順
 * - FINISHEDとCANCELEDは他のステータスの下に配置
 */
const sortSprintsByNumWithFinishedLast = (
  a: SprintDetail,
  b: SprintDetail
): number => {
  const aFinished = isFinishedStatus(a.status)
  const bFinished = isFinishedStatus(b.status)
  // 終了済みかどうかでグループ分け
  if (aFinished !== bFinished) {
    return aFinished ? 1 : -1
  }
  // 同じグループ内ではnum順
  return a.num - b.num
}

type UseSprintColumnParams = {
  getWbsItemProjectUuid: (
    row: ProjectPlanNewRow | undefined
  ) => string | undefined
  getTeamUuid: (row: ProjectPlanNewRow | undefined) => string | undefined
  useSprintsMultiSelect?: boolean
}

/** スプリント配列の重複除去とnum順ソート */
const dedupeAndSortSprints = (
  sprints: SprintDetail[] | undefined
): SprintDetail[] | undefined => {
  if (!sprints) return sprints
  const uniqueSprints = sprints.filter(
    (s, index, self) => index === self.findIndex(t => t.uuid === s.uuid)
  )
  return uniqueSprints.sort((a, b) => a.num - b.num)
}

/**
 * 編集可能な複数スプリント選択列を返すフック
 * ProjectPlanNew専用の実装
 */
const useEditableSprintsColumn = ({
  getWbsItemProjectUuid,
  getTeamUuid,
}: {
  getWbsItemProjectUuid: (
    row: ProjectPlanNewRow | undefined
  ) => string | undefined
  getTeamUuid: (row: ProjectPlanNewRow | undefined) => string | undefined
}): BulkSheetProperty => {
  const property = useMemo(() => {
    return {
      field: FIELD_SPRINTS,
      headerName: intl.formatMessage({ id: 'wbsItem.property.sprint' }),
      hide: false,
      width: 120,
      editable: true,
      type: [ColumnType.autocomplete],
      valueSetter: (params: ValueSetterParams) => {
        const sprints: SprintDetail[] = params.newValue
        if (sprints) {
          params.newValue = dedupeAndSortSprints(sprints)
        }
        return defaultValueSetter(params)
      },
      valueFormatter: (params: ValueFormatterParams) => {
        const sprints: SprintDetail[] | undefined = params.value
        if (!sprints || sprints.length === 0) return ''
        return sprints.map(s => s.name).join(', ')
      },
      cellRenderer: MultiAutocompleteCell.cellRenderer,
      cellRendererParams: {
        showIcon: false,
        sortValue: dedupeAndSortSprints,
      },
      cellEditor: MultiAutocompleteCellEditor,
      cellEditorParams: {
        search: async (
          text: string,
          _projectUuid: string,
          data: ProjectPlanNewRow
        ) => {
          const teamUuid = getTeamUuid(data)
          if (!teamUuid) return []
          const projectUuid = getWbsItemProjectUuid(data)
          const results = await Sprint.searchByText(teamUuid, text)
          return results.filter(
            (option: SprintDetail) =>
              option.projectUuid === projectUuid && option.teamUuid === teamUuid
          )
        },
        label: (option: SprintDetail) => option.name,
        showIcon: false,
        sortOptions: (options: SprintDetail[]) =>
          options.sort(sortSprintsByNumWithFinishedLast),
        disableOption: (_params, option: SprintDetail) =>
          isFinishedStatus(option.status),
      },
      suppressKeyboardEvent: params =>
        (!params.editing &&
          ['Delete', 'Backspace'].includes(params.event.key)) ||
        (params.editing && params.event.key === 'Enter'),
      filter: ServerSideSelectFilter,
      getValuesForOptionFilter: async (context: any) => {
        const response = await Sprint.getFilterOptionUuids({
          rootProjectPlanUuid: context.rootProjectPlan?.uuid,
        })
        return response.json
      },
      comparator: (a: SprintDetail[], b: SprintDetail[]) => {
        // 最初のスプリントのnum順でソート（セル内表示と一貫性を保つ）
        const aNum = a?.[0]?.num ?? Number.MAX_SAFE_INTEGER
        const bNum = b?.[0]?.num ?? Number.MAX_SAFE_INTEGER
        return aNum - bNum
      },
    }
  }, [getWbsItemProjectUuid, getTeamUuid])

  return property
}

/**
 * スプリント列を返すフック
 * Feature Flagに応じて単一選択/複数選択を切り替える
 */
export const useSprintColumn = ({
  getWbsItemProjectUuid,
  getTeamUuid,
  useSprintsMultiSelect,
}: UseSprintColumnParams) => {
  const sprintColSingle = useSprint({
    field: FIELD_SPRINT,
    getWbsItemProjectUuid,
    getTeamUuid,
    hide: false,
  })

  const sprintColMulti = useEditableSprintsColumn({
    getWbsItemProjectUuid,
    getTeamUuid,
  })

  const sprintCol = useMemo(
    () => (useSprintsMultiSelect ? sprintColMulti : sprintColSingle),
    [useSprintsMultiSelect, sprintColMulti, sprintColSingle]
  )

  return sprintCol
}

/**
 * チーム変更時にスプリントをクリアするコールバックを返すフック
 */
export const useClearSprintOnTeamChange = (useSprintsMultiSelect?: boolean) => {
  const clearSprintOnTeamChange = useCallback(
    (params: ValueSetterParams<ProjectPlanNewRow>) => {
      const oldTeam = params.oldValue
      const newTeam = params.newValue

      if (useSprintsMultiSelect) {
        // 複数スプリント選択モード
        if (
          oldTeam?.uuid !== newTeam?.uuid &&
          params.data.body?.wbsItem?.sprints?.length
        ) {
          if (!params.data.editedData) {
            params.data.editedData = {
              [FIELD_SPRINTS]: params.data.body.wbsItem.sprints,
            }
          } else if (!params.data.editedData.hasOwnProperty(FIELD_SPRINTS)) {
            params.data.editedData[FIELD_SPRINTS] =
              params.data.body.wbsItem.sprints
          }
          params.data.body.wbsItem.sprints = []
        }
      } else {
        // 単一スプリント選択モード
        if (
          oldTeam?.uuid !== newTeam?.uuid &&
          !!params.data.body?.wbsItem?.sprint
        ) {
          if (!params.data.editedData) {
            params.data.editedData = {
              [FIELD_SPRINT]: params.data.body.wbsItem.sprint,
            }
          } else if (!params.data.editedData.hasOwnProperty(FIELD_SPRINT)) {
            params.data.editedData[FIELD_SPRINT] =
              params.data.body.wbsItem.sprint
          }
          params.data.body.wbsItem.sprint = undefined
        }
      }
    },
    [useSprintsMultiSelect]
  )

  return clearSprintOnTeamChange
}
